import type {ContinuationSpec} from './continuationSpec';

/** NetworkAccessResult is the user's decision on a network access request. */
export type NetworkAccessResult =
  | {kind: 'grant'; domains: string[]}
  | {kind: 'deny'};

/**
 * networkAccessContinuation turns a grant/deny decision into the next-turn
 * prompt and permissions patch. The patch replaces the inherited allowlist, so
 * it always carries the complete desired set; descendant turns inherit it.
 */
export const networkAccessContinuation: ContinuationSpec<NetworkAccessResult> =
  {
    toolName: 'request_network_access',
    composerPolicy: 'block',
    parseResult(value: unknown): NetworkAccessResult {
      const kind = (value as {kind?: unknown} | null)?.kind;
      if (kind === 'deny') {
        return {kind: 'deny'};
      }

      if (kind === 'grant') {
        const domains = (value as {domains?: unknown}).domains;
        if (
          Array.isArray(domains) &&
          domains.every(domain => typeof domain === 'string')
        ) {
          return {kind: 'grant', domains};
        }
      }

      throw new Error(
        'network access result must grant string domains or deny'
      );
    },
    buildContinuation(payload, result, {activeNetworkDomains}) {
      if (payload.kind !== 'network-access') {
        throw new Error(
          'network access continuation requires a pending request'
        );
      }

      if (result.kind === 'deny') {
        // Deny continues without changing the active allowlist.
        return {
          parts: [
            {
              type: 'text',
              text: "I'm not granting network access. Please proceed without it.",
              status: 'complete',
            },
          ],
          permissions: {allowed_network_domains: activeNetworkDomains},
        };
      }

      const domains = Array.from(
        new Set([...activeNetworkDomains, ...result.domains])
      );
      return {
        parts: [
          {
            type: 'text',
            text: `I've granted network access to: ${result.domains.join(', ')}. Please continue.`,
            status: 'complete',
          },
        ],
        permissions: {allowed_network_domains: domains},
      };
    },
  };
